/**
 * Échelle d'ombres « soft layered ».
 *
 * Principe : au lieu d'une ombre unique et dure, chaque niveau combine
 *  - un halo de contour très léger (1px) qui remplace la bordure,
 *  - une ombre de contact courte,
 *  - une ombre d'ambiance longue et diffuse.
 * Le rendu est plus proche des UI modernes (Linear, Vercel, Stripe) que
 * du Material Design d'origine.
 */

const light = (contact, ambient, spread = 'rgba(15, 23, 42, 0.04)') => `0 0 0 1px ${spread}, ${contact}, ${ambient}`;

export const lightShadows = [
  'none',
  light('0 1px 2px rgba(15, 23, 42, 0.04)', '0 1px 3px rgba(15, 23, 42, 0.04)'),
  light('0 1px 2px rgba(15, 23, 42, 0.05)', '0 2px 6px rgba(15, 23, 42, 0.05)'),
  light('0 1px 2px rgba(15, 23, 42, 0.05)', '0 4px 10px -2px rgba(15, 23, 42, 0.06)'),
  light('0 1px 3px rgba(15, 23, 42, 0.05)', '0 6px 14px -4px rgba(15, 23, 42, 0.07)'),
  light('0 2px 4px rgba(15, 23, 42, 0.05)', '0 8px 18px -6px rgba(15, 23, 42, 0.08)'),
  light('0 2px 4px rgba(15, 23, 42, 0.05)', '0 10px 22px -8px rgba(15, 23, 42, 0.09)'),
  light('0 2px 5px rgba(15, 23, 42, 0.06)', '0 12px 26px -8px rgba(15, 23, 42, 0.10)'),
  light('0 3px 6px rgba(15, 23, 42, 0.06)', '0 14px 30px -10px rgba(15, 23, 42, 0.11)'),
  light('0 3px 6px rgba(15, 23, 42, 0.06)', '0 16px 34px -10px rgba(15, 23, 42, 0.12)'),
  light('0 4px 8px rgba(15, 23, 42, 0.06)', '0 18px 38px -12px rgba(15, 23, 42, 0.13)'),
  light('0 4px 8px rgba(15, 23, 42, 0.07)', '0 20px 42px -12px rgba(15, 23, 42, 0.14)'),
  light('0 5px 10px rgba(15, 23, 42, 0.07)', '0 22px 46px -14px rgba(15, 23, 42, 0.15)'),
  light('0 5px 10px rgba(15, 23, 42, 0.07)', '0 24px 50px -14px rgba(15, 23, 42, 0.16)'),
  light('0 6px 12px rgba(15, 23, 42, 0.07)', '0 26px 54px -16px rgba(15, 23, 42, 0.17)'),
  light('0 6px 12px rgba(15, 23, 42, 0.08)', '0 28px 58px -16px rgba(15, 23, 42, 0.18)'),
  light('0 7px 14px rgba(15, 23, 42, 0.08)', '0 30px 62px -18px rgba(15, 23, 42, 0.19)'),
  light('0 7px 14px rgba(15, 23, 42, 0.08)', '0 32px 66px -18px rgba(15, 23, 42, 0.20)'),
  light('0 8px 16px rgba(15, 23, 42, 0.08)', '0 34px 70px -20px rgba(15, 23, 42, 0.21)'),
  light('0 8px 16px rgba(15, 23, 42, 0.09)', '0 36px 74px -20px rgba(15, 23, 42, 0.22)'),
  light('0 9px 18px rgba(15, 23, 42, 0.09)', '0 38px 78px -22px rgba(15, 23, 42, 0.23)'),
  light('0 9px 18px rgba(15, 23, 42, 0.09)', '0 40px 82px -22px rgba(15, 23, 42, 0.24)'),
  light('0 10px 20px rgba(15, 23, 42, 0.10)', '0 42px 86px -24px rgba(15, 23, 42, 0.25)'),
  light('0 10px 20px rgba(15, 23, 42, 0.10)', '0 44px 90px -24px rgba(15, 23, 42, 0.26)'),
  light('0 12px 24px rgba(15, 23, 42, 0.10)', '0 48px 96px -26px rgba(15, 23, 42, 0.28)')
];

const dark = (contact, ambient) => `0 0 0 1px rgba(148, 163, 184, 0.08), ${contact}, ${ambient}`;

export const darkShadows = [
  'none',
  dark('0 1px 2px rgba(0, 0, 0, 0.32)', '0 1px 3px rgba(0, 0, 0, 0.24)'),
  dark('0 1px 2px rgba(0, 0, 0, 0.34)', '0 2px 6px rgba(0, 0, 0, 0.28)'),
  dark('0 1px 2px rgba(0, 0, 0, 0.36)', '0 4px 10px -2px rgba(0, 0, 0, 0.32)'),
  dark('0 1px 3px rgba(0, 0, 0, 0.36)', '0 6px 14px -4px rgba(0, 0, 0, 0.34)'),
  dark('0 2px 4px rgba(0, 0, 0, 0.38)', '0 8px 18px -6px rgba(0, 0, 0, 0.36)'),
  dark('0 2px 4px rgba(0, 0, 0, 0.38)', '0 10px 22px -8px rgba(0, 0, 0, 0.38)'),
  dark('0 2px 5px rgba(0, 0, 0, 0.40)', '0 12px 26px -8px rgba(0, 0, 0, 0.40)'),
  dark('0 3px 6px rgba(0, 0, 0, 0.40)', '0 14px 30px -10px rgba(0, 0, 0, 0.42)'),
  dark('0 3px 6px rgba(0, 0, 0, 0.42)', '0 16px 34px -10px rgba(0, 0, 0, 0.44)'),
  dark('0 4px 8px rgba(0, 0, 0, 0.42)', '0 18px 38px -12px rgba(0, 0, 0, 0.46)'),
  dark('0 4px 8px rgba(0, 0, 0, 0.44)', '0 20px 42px -12px rgba(0, 0, 0, 0.48)'),
  dark('0 5px 10px rgba(0, 0, 0, 0.44)', '0 22px 46px -14px rgba(0, 0, 0, 0.50)'),
  dark('0 5px 10px rgba(0, 0, 0, 0.46)', '0 24px 50px -14px rgba(0, 0, 0, 0.52)'),
  dark('0 6px 12px rgba(0, 0, 0, 0.46)', '0 26px 54px -16px rgba(0, 0, 0, 0.54)'),
  dark('0 6px 12px rgba(0, 0, 0, 0.48)', '0 28px 58px -16px rgba(0, 0, 0, 0.56)'),
  dark('0 7px 14px rgba(0, 0, 0, 0.48)', '0 30px 62px -18px rgba(0, 0, 0, 0.58)'),
  dark('0 7px 14px rgba(0, 0, 0, 0.50)', '0 32px 66px -18px rgba(0, 0, 0, 0.60)'),
  dark('0 8px 16px rgba(0, 0, 0, 0.50)', '0 34px 70px -20px rgba(0, 0, 0, 0.62)'),
  dark('0 8px 16px rgba(0, 0, 0, 0.52)', '0 36px 74px -20px rgba(0, 0, 0, 0.64)'),
  dark('0 9px 18px rgba(0, 0, 0, 0.52)', '0 38px 78px -22px rgba(0, 0, 0, 0.66)'),
  dark('0 9px 18px rgba(0, 0, 0, 0.54)', '0 40px 82px -22px rgba(0, 0, 0, 0.68)'),
  dark('0 10px 20px rgba(0, 0, 0, 0.54)', '0 42px 86px -24px rgba(0, 0, 0, 0.70)'),
  dark('0 10px 20px rgba(0, 0, 0, 0.56)', '0 44px 90px -24px rgba(0, 0, 0, 0.72)'),
  dark('0 12px 24px rgba(0, 0, 0, 0.56)', '0 48px 96px -26px rgba(0, 0, 0, 0.74)')
];
